import axios from 'axios'
import type { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { tokenManager } from './tokenManager'
import { authService } from './auth'
import { useAuthStore } from '@/stores/auth'

export type ApiClientConfig = {
  baseURL: string
  timeout?: number
  maxRetries?: number
  retryDelay?: number
}

export class ApiError extends Error {
  statusCode?: number
  originalError?: Error

  constructor(
    message: string,
    statusCode?: number,
    originalError?: Error
  ) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.originalError = originalError
  }
}

export class ApiClient {
  private client: AxiosInstance
  private maxRetries: number
  private retryDelay: number
  private refreshInProgress: Promise<void> | null = null
  private csrfToken: string | null = null

  constructor(config: ApiClientConfig) {
    this.maxRetries = config.maxRetries ?? 3
    this.retryDelay = config.retryDelay ?? 1000

    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout ?? 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Load CSRF token from sessionStorage on initialization
    this.csrfToken = sessionStorage.getItem('csrf_token')

    this.setupInterceptors()
  }

  /**
   * Generate and store a new CSRF token
   * Should be called after successful login
   */
  generateCsrfToken(): string {
    // Generate a random CSRF token (32 bytes = 64 hex characters)
    const array = new Uint8Array(32)
    crypto.getRandomValues(array)
    const token = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
    
    // Store in sessionStorage
    sessionStorage.setItem('csrf_token', token)
    this.csrfToken = token
    
    return token
  }

  /**
   * Clear CSRF token (should be called on logout)
   */
  clearCsrfToken(): void {
    sessionStorage.removeItem('csrf_token')
    this.csrfToken = null
  }

  private setupInterceptors() {
    // Request interceptor - attach Authorization header with ID token
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Get ID token from token manager (contains user email and profile info)
        const idToken = tokenManager.getIdToken()
        
        if (idToken) {
          // Attach Authorization header with Bearer token
          config.headers.Authorization = `Bearer ${idToken}`
        }
        
        // Add CSRF token to state-changing requests (POST, PUT, DELETE, PATCH)
        const method = config.method?.toUpperCase()
        if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
          if (this.csrfToken) {
            config.headers['X-CSRF-Token'] = this.csrfToken
          } else {
            console.warn('CSRF token not found for state-changing request')
          }
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor - handle 401 errors and automatic token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

        // Check if this is a 401 Unauthorized error
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          // Mark request as retried to prevent infinite loops
          originalRequest._retry = true

          try {
            // Attempt token refresh
            await this.refreshAccessToken()

            // Get new ID token
            const newIdToken = tokenManager.getIdToken()
            
            if (newIdToken) {
              // Update Authorization header with new token
              originalRequest.headers.Authorization = `Bearer ${newIdToken}`
              
              // Retry original request with new token
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Token refresh failed - clear auth and redirect to login
            console.error('Token refresh failed:', refreshError)
            this.handleAuthenticationFailure()
            return Promise.reject(refreshError)
          }
        }

        // For other errors, use existing error handler
        return this.handleError(error)
      }
    )
  }

  /**
   * Refresh access token using refresh token
   * 
   * Handles concurrent refresh requests by ensuring only one refresh
   * operation is in progress at a time.
   */
  private async refreshAccessToken(): Promise<void> {
    // If refresh is already in progress, wait for it to complete
    if (this.refreshInProgress) {
      return this.refreshInProgress
    }

    // Start new refresh operation
    this.refreshInProgress = (async () => {
      try {
        const refreshToken = tokenManager.getRefreshToken()
        
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        // Call auth service to refresh token
        const tokenResponse = await authService.refreshToken(refreshToken)

        // Store new tokens
        await tokenManager.storeTokens({
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
          idToken: tokenResponse.id_token,
        })

        // Update auth store
        const authStore = useAuthStore()
        authStore.setTokens({
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
          idToken: tokenResponse.id_token,
        })

        console.log('Token refresh successful')
      } catch (error) {
        console.error('Token refresh error:', error)
        throw error
      } finally {
        // Clear refresh in progress flag
        this.refreshInProgress = null
      }
    })()

    return this.refreshInProgress
  }

  /**
   * Handle authentication failure by clearing auth state and redirecting to login
   */
  private handleAuthenticationFailure(): void {
    // Clear tokens
    tokenManager.clearTokens()

    // Clear CSRF token
    this.clearCsrfToken()

    // Clear auth store
    const authStore = useAuthStore()
    authStore.clearAuth()

    // Redirect to login page
    // Use window.location to ensure full page reload and clear any cached state
    window.location.href = '/login'
  }

  private async handleError(error: AxiosError): Promise<never> {
    // Network error (no response)
    if (!error.response) {
      throw new ApiError(
        'Network error: Unable to reach the server. Please check your connection.',
        undefined,
        error
      )
    }

    const { status, data } = error.response

    // Handle specific status codes
    switch (status) {
      case 401:
        throw new ApiError('Unauthorized: Please log in again.', 401, error)
      case 403:
        throw new ApiError('Forbidden: You do not have permission to access this resource.', 403, error)
      case 404:
        throw new ApiError('Not found: The requested resource does not exist.', 404, error)
      case 429:
        throw new ApiError('Too many requests: Please try again later.', 429, error)
      case 500:
      case 502:
      case 503:
      case 504:
        throw new ApiError(
          'Server error: The server encountered an error. Please try again later.',
          status,
          error
        )
      default:
        const message = (data as any)?.message || error.message || 'An unexpected error occurred'
        throw new ApiError(message, status, error)
    }
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    attempt: number = 0
  ): Promise<T> {
    try {
      return await requestFn()
    } catch (error) {
      const apiError = error as ApiError

      // Don't retry client errors (4xx) except 429 (rate limit)
      if (apiError.statusCode && apiError.statusCode >= 400 && apiError.statusCode < 500 && apiError.statusCode !== 429) {
        throw error
      }

      // Retry on network errors or 5xx errors
      if (attempt < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, attempt) // Exponential backoff
        console.warn(`Request failed, retrying in ${delay}ms (attempt ${attempt + 1}/${this.maxRetries})`)
        await this.sleep(delay)
        return this.retryRequest(requestFn, attempt + 1)
      }

      throw error
    }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.get<T>(url, config)
      return response.data
    })
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.post<T>(url, data, config)
      return response.data
    })
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.put<T>(url, data, config)
      return response.data
    })
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.delete<T>(url, config)
      return response.data
    })
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return this.retryRequest(async () => {
      const response = await this.client.patch<T>(url, data, config)
      return response.data
    })
  }
}

// Use environment variable or default to localhost
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
export const apiClient = new ApiClient({
  baseURL: apiBaseUrl,
  timeout: 30000,
  maxRetries: 3,
  retryDelay: 1000,
})
